/**
 * productConfig — the declarative PROFILE for each product line.
 *
 * `productMode` (the ONE project flag) selects a ViewerBackend adapter (see
 * `getBackend`) AND a profile here that declares WHICH viewer modules/polls run.
 * Modules read a NAMED capability off this profile (e.g. `pollsNvrClock`) instead
 * of testing the mode — so there is never an `if (cloud)` sprinkled across
 * components, and adding a product line (EEN) is one row here, not a hunt through
 * every module.
 *
 * The local NVR viewer and the cloud (nvr.cloud) viewer are DIFFERENT products:
 *   - LOCAL is served BY an NVR at its IP; its backend is that NVR's rda-backend
 *     (JSON-RPC). It has a server list, jump, eView, a live NVR clock, and a
 *     system-status panel — all backed by local JSON-RPC session calls.
 *   - CLOUD is a hosted viewer at nvr.cloud.dividia.net whose SINGULAR backend is
 *     cloudapi (`/ve` REST + JWT). There is NO NVR to poll: no server list, no
 *     jump, no local session verification, no NVR clock, no machine-status poll.
 *     Those modules must NOT run — otherwise they call a local NVR that isn't
 *     there and trip `noauth → EXPIRE_SESSION`, bouncing the cloud session.
 */
import { PRODUCT_MODE, getProductMode } from './productMode';

/**
 * @typedef {Object} ProductProfile
 * @property {boolean} hasLocalNvrSession - session validity is checked against a
 *   local NVR (App: getServer / checkExists / the verifySession poll). Cloud
 *   trusts the JWT instead, so this is false there.
 * @property {boolean} hasServerList - LoginView shows a server dropdown and polls
 *   each server's online status (fetchConnectedServers / checkServerStatuses).
 * @property {boolean} hasServerBox - LoginView shows the manual server-address BOX
 *   (IP / hostname / store-name input + Set button + Online/Offline status + the
 *   target-NVR "Version" line). This is the "which local NVR am I connecting to"
 *   widget — meaningless in CLOUD, where cloudapi is the single fixed backend and
 *   the login page shows only the app version. Distinct from hasServerList (the
 *   dropdown): gated on this so the box is ALWAYS shown in local and NEVER in cloud
 *   (David 2026-08-20), without overloading hasServerList / hasLocalNvrSession.
 * @property {boolean} pollsNvrClock - LiveView polls getNvrTimestamp once a second
 *   for the clock overlay (cloud gets the clock from the camera metadata frame).
 * @property {boolean} pollsSystemStatus - StatusView polls machine status +
 *   users-online.
 * @property {boolean} hasJpegBaseLayer - the live grid renders an always-on JPEG
 *   base layer (local mpengine `/mpe/camN.jpg`) under the WS overlay. Cloud has
 *   NO JPEG fallback (there is no mpengine): every cloud cam (dragon and legacy
 *   alike) is WebCodecs H.264-over-WS, so each cell is a single player (no MSE,
 *   no JPEG layer). False for cloud.
 * @property {?string} viewerName - product display name shown in the header /
 *   login title. `null` for local, which keeps its own dView-vs-eView title
 *   logic; cloud overrides it with a single brand name.
 * @property {boolean} hasJump - whether the viewer shows a "jump" control at all.
 *   LOCAL jumps between NVR servers. CLOUD drops it entirely (David): the viewer
 *   surfaces ALL devices' cameras at once (all-devices grid) and saved VIEWS are the
 *   sole navigation, so a per-device jump menu is redundant. When false, JumpControl
 *   renders nothing.
 * @property {'server'|'device'} jumpKind - which jump control shows when hasJump is
 *   true. LOCAL jumps NVR servers (ServerView). (CLOUD has hasJump false, so this is
 *   moot there; kept 'device' for when/if a cloud device-jump is reintroduced.)
 * @property {boolean} hasViewerDownloads - the login/nav shows links to DOWNLOAD
 *   or launch the standalone viewers: the Java DESKTOP dview "Full Viewer" /
 *   "Enterprise Viewer" (launcher.php / dview.php / eview.php) AND the native
 *   Mac / iOS / Android apps (App Store links). Cloud IS the hosted product —
 *   you're already in the viewer and there's no on-box Java build behind it — so
 *   all of these are hidden there (false).
 *
 * Playback nav: legacy single-cam playback is retired. Every product's Playback IS the
 * multi-cam view (route /mcplayback), labeled simply "Playback" everywhere. The button's
 * visibility is gated on the fPlayback USER RIGHT at each call site (state.user.fPlayback —
 * getRights RPC locally, /ve/login payload in cloud), NOT a product capability — so there is
 * deliberately no hasLegacyPlayback / hasMultiCamPlayback / multiCamNavLabel flag here.
 */

/** Local NVR viewer — every module on (this is the default for unknown modes). */
const LOCAL_PROFILE = Object.freeze({
  hasLocalNvrSession: true,
  hasServerList: true,
  hasServerBox: true,
  pollsNvrClock: true,
  pollsSystemStatus: true,
  hasJpegBaseLayer: true,
  // null → call sites keep the existing dView/eView title logic.
  viewerName: null,
  hasJump: true,
  jumpKind: 'server',
  hasViewerDownloads: true,
  // Setup is the SAME shared /setup shell (SetupView) for EVERY product (David 2026-08-05) — ALWAYS
  // available to every user; the shell shows the modules this product exposes AND the user's rights
  // allow (per-module gating via SETUP_MODULES[id].right — Display ungated, config modules ADMIN; there
  // is NO whole-shell gate). Local exposes ONLY Display today (viewer prefs — theme/overlays, client-side,
  // no NVR backend); the Java config modules (System/Device/Users/Camera/…) land later + register then.
  setup: {
    modules: ['display'],
    camera: { motionEditor: 'windows', richConfig: 'capability', canAddCamera: true, canDiscover: false },
    users: { model: 'bitmask', accessScope: 'camera', orgModel: 'enterprise' },
  },
});

/** Cloud (nvr.cloud) viewer — cloudapi is the only backend; no local-NVR modules. */
const CLOUD_PROFILE = Object.freeze({
  hasLocalNvrSession: false,
  hasServerList: false,
  hasServerBox: false,
  pollsNvrClock: false,
  pollsSystemStatus: false,
  hasJpegBaseLayer: false,
  // Product name shown in the header AND the browser tab title (index.js) for the cloud viewer.
  // "Dividia Cloud" matches CloudNvrAdapter.getSystemLabel()'s multi-customer/no-name label so the
  // two never disagree (#86bc2p0fh — was 'Cloud NVR').
  viewerName: 'Dividia Cloud',
  hasJump: false,
  jumpKind: 'device',
  hasViewerDownloads: false,
  // Setup (unified-viewer-setup-design.md §1.1). Cloud v1 implements display + cameras + users;
  // the org modules scaffold as stubs (SetupModuleStub) for the fast-follow.
  // NOTE (David 2026-07-20): a cloud "device" IS the edge NVR box (self-registered into cloudapi
  // on first connect) — NOT the local "device-type" container concept. Cloud has no monarch, so
  // its config is set here (via /ve → sConfig → engine_backend). The registry labels it distinctly.
  setup: {
    // No whole-shell gate (David 2026-08-05): the shell is always open; each module gates on its own
    // right (SETUP_MODULES[id].right) — so a non-admin cloud user sees just Display, an admin sees all.
    // Order = the cloud org flow top-down (Customer ▸ Site ▸ Device/NVR ▸ Camera), then Users (access),
    // then Display (viewer prefs) pinned far-right (David 2026-07-20).
    modules: [
      'customers', // Dividia-level org (bRole=1)
      'sites',     // customer→site org (JSCustomerProject)
      'devices',   // the edge NVR boxes + customer/site assignment (self-registered)
      'cameras',   // General/Video/PTZ/LPR, capability-gated (dragon rich vs legacy thin)
      'users',     // bRole tier + granular rights (bRights) + JSAcl site access
      'display',   // shared viewer prefs (overlayPrefs-backed) — always far-right
    ],
    // canDiscover: cloud Dragon devices can run an ONVIF scan of their LAN (via cloudapi →
    // backend /ctrl) and add found cameras. This is the ONLY "add a camera" path in cloud
    // (canAddCamera stays false — no blank-form manual add), so the two coexist.
    camera: { motionEditor: 'grid', richConfig: 'capability', canAddCamera: false, canDiscover: true },
    users: { model: 'bRole+rights', accessScope: 'site', orgModel: 'customer-project' },
  },
});

const PROFILES = {
  [PRODUCT_MODE.LOCAL]: LOCAL_PROFILE,
  [PRODUCT_MODE.CLOUD]: CLOUD_PROFILE,
  // EEN not built yet; until it declares a profile it inherits the LOCAL module
  // set via the fallback below (safe default — everything on — for the product
  // that actually ships today).
};

/**
 * The active product profile. Env/state select the mode (see `getProductMode`);
 * an unknown mode falls back to the LOCAL profile so every existing (non-cloud)
 * build is unchanged.
 * @param {Object} [state] - Redux state (optional; env drives the cloud deploy)
 * @returns {ProductProfile}
 */
export const getProductConfig = (state) => PROFILES[getProductMode(state)] || LOCAL_PROFILE;
